import React, { useState, useEffect, useRef } from 'react';
import { leaderboardService } from '../services/leaderboardService';
import { PencilIcon, TrashIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import ModalBasic from '../components/ModalBasic';
import { usePopper } from 'react-popper';
import { motion, AnimatePresence } from 'framer-motion';

const ManageLeaderboard = ({ campaignId }) => {
  const [entries, setEntries] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const inputRef = useRef(null);
  const saveButtonRef = useRef(null);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'right',
    modifiers: [
      { name: 'offset', options: { offset: [0, 0] } },
      {
        name: 'preventOverflow',
        options: {
          padding: 8,
        },
      }
    ],
  });

  useEffect(() => {
    loadEntries();
    loadTotalEntries();
  }, [campaignId]);

  useEffect(() => {
    if (showDeleteModal) {
      const timer = setTimeout(() => {
        setModalOpen(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showDeleteModal]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (inputRef.current && !inputRef.current.contains(event.target) && 
          (!saveButtonRef.current || !saveButtonRef.current.contains(event.target))) {
        setEditingId(null);
        setEditName('');
      }
    }

    if (editingId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [editingId]);

  const loadEntries = async () => {
    setIsLoading(true);
    try {
      // Ensure we only get top 10 entries
      const { entries } = await leaderboardService.getTopScores(campaignId, 10);
      setEntries(entries);
    } catch (error) {
      console.error('Error loading leaderboard entries:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTotalEntries = async () => {
    try {
      const count = await leaderboardService.getTotalEntries(campaignId);
      setTotalEntries(count);
    } catch (error) {
      console.error('Error loading total leaderboard entries:', error);
    }
  };

  const handleDelete = async () => {
    if (!entryToDelete) return;
    
    try {
      await leaderboardService.deleteEntry(campaignId, entryToDelete);
      setModalOpen(false);
      setShowDeleteModal(false);
      setEntryToDelete(null);
      await loadEntries(); // Reload the list
    } catch (error) {
      console.error('Error deleting entry:', error);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      setShowDeleteModal(false);
      setEntryToDelete(null);
    }, 300);
  };

  const handleEdit = (entry) => {
    setEditingId(entry.id);
    setEditName(entry.name);
    setEditEmail(entry.email || '');
  };

  const handleSave = async () => {
    if (!editingId) return;
    
    try {
      await leaderboardService.updateScore(campaignId, editingId, { 
        name: editName,
        email: editEmail
      });
      await loadEntries();
      
      setEditingId(null);
      setEditName('');
      setEditEmail('');
    } catch (error) {
      console.error('Error updating entry:', error);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      // Fetch all entries for download
      const { entries: allEntries } = await leaderboardService.getAllScores(campaignId);
      
      // Convert entries to CSV format
      const headers = ['Rank', 'Name', 'Email', 'Score'];
      const csvData = allEntries.map((entry, index) => [
        index + 1,
        entry.name,
        entry.email || 'No email provided',
        entry.score
      ]);
      
      // Add headers to the beginning
      csvData.unshift(headers);
      
      // Convert to CSV string
      const csvString = csvData
        .map(row => row.map(cell => 
          cell.toString().includes(',') ? `"${cell}"` : cell
        ).join(','))
        .join('\n');
      
      // Create blob and download
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `leaderboard-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading leaderboard:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Get trophy emoji for top 3 rankings
  const getTrophyEmoji = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return null;
    }
  };

  // Get rank styling based on position
  const getRankStyling = (rank) => {
    if (rank <= 3) {
      return {
        background: 'linear-gradient(135deg, #F1C40F, #E67E22)',
        border: '2px solid #E74C3C',
        color: '#2C3E50',
        textShadow: '1px 1px 0px #FFFFFF'
      };
    }
    return {
      background: 'rgba(52, 73, 94, 0.8)',
      border: '1px solid rgba(241, 196, 15, 0.3)',
      color: '#F1C40F'
    };
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-xl p-5 shadow-lg"
      style={{
        background: 'linear-gradient(135deg, #2C3E50, #34495E)',
        border: '2px solid #F1C40F',
        fontFamily: '"Share Tech Mono", monospace'
      }}
    >
      {/* Header */}
      <div className="mb-6">
        {/* Title and Stats Row */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-2xl">🏆</span>
          <h3 className="text-lg font-bold tracking-wider" style={{
            color: '#F1C40F',
            textShadow: '1px 1px 0px #E67E22'
          }}>
            LEADERBOARD
          </h3>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="text-sm font-bold" style={{color: '#ECF0F1'}}>
            Total Participants: <span style={{color: '#2ECC71'}}>{totalEntries}</span>
          </div>
          
          {/* Download Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleDownload}
            disabled={isDownloading}
            className="relative group self-start sm:self-auto"
            style={{
              background: isDownloading 
                ? 'rgba(52, 73, 94, 0.6)' 
                : 'linear-gradient(135deg, #E74C3C, #C0392B)',
              border: '2px solid #F1C40F',
              borderRadius: '8px',
              padding: '8px 12px',
              transition: 'all 0.2s ease'
            }}
          >
            {/* Tooltip */}
            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block z-50">
              <div className="px-3 py-2 rounded-lg text-xs font-bold whitespace-nowrap" style={{
                background: '#2C3E50',
                border: '1px solid #F1C40F',
                color: '#F1C40F'
              }}>
                📥 DOWNLOAD FULL REPORT
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isDownloading ? (
                <div className="animate-spin w-4 h-4" style={{color: '#95A5A6'}}>
                  ⚙️
                </div>
              ) : (
                <ArrowDownTrayIcon className="w-4 h-4" style={{color: '#FFFFFF'}} />
              )}
              <span className="text-xs font-bold" style={{color: '#FFFFFF'}}>
                {isDownloading ? 'DOWNLOADING...' : 'DOWNLOAD'}
              </span>
            </div>
          </motion.button>
        </div>
      </div>
      
      {/* Leaderboard Entries */}
      <div className="space-y-2">
        <AnimatePresence>
          {isLoading ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-8"
              style={{color: '#95A5A6'}}
            >
              <div className="text-2xl mb-2">⚡</div>
              <div className="font-bold">LOADING DATA...</div>
            </motion.div>
          ) : entries.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-8 rounded-lg"
              style={{
                background: 'rgba(52, 73, 94, 0.6)',
                border: '1px solid rgba(149, 165, 166, 0.3)',
                color: '#95A5A6'
              }}
            >
              <div className="text-3xl mb-2">📊</div>
              <div className="font-bold">NO PARTICIPANTS YET</div>
              <div className="text-xs mt-1">Leaderboard will populate once users start playing</div>
            </motion.div>
          ) : (
            entries.map((entry, index) => {
              const rank = index + 1;
              const trophyEmoji = getTrophyEmoji(rank);
              const rankStyling = getRankStyling(rank);
              
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleEdit(entry)}
                  className="flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all duration-200"
                  style={{
                    background: rank <= 3 
                      ? 'linear-gradient(135deg, rgba(241, 196, 15, 0.1), rgba(230, 126, 34, 0.1))'
                      : 'rgba(52, 73, 94, 0.4)',
                    border: rank <= 3 
                      ? '2px solid rgba(241, 196, 15, 0.4)' 
                      : '1px solid rgba(149, 165, 166, 0.2)'
                  }}
                >
                  <div className="flex items-center gap-4">
                    {/* Rank Badge */}
                    <div className="flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm" style={rankStyling}>
                      {trophyEmoji || rank}
                    </div>
                    
                    {/* Player Info */}
                    <div 
                      ref={editingId === entry.id ? setReferenceElement : null}
                      className="relative"
                    >
                      <div className="font-bold text-sm" style={{color: '#ECF0F1'}}>
                        {entry.name}
                      </div>
                      {entry.email && (
                        <div className="text-xs" style={{color: '#95A5A6'}}>
                          {entry.email}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {/* Score */}
                    <div className="text-right">
                      <div className="font-bold text-lg" style={{color: '#2ECC71'}}>
                        {entry.score.toLocaleString()}
                      </div>
                      <div className="text-xs" style={{color: '#95A5A6'}}>
                        POINTS
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEntryToDelete(entry.id);
                        setShowDeleteModal(true);
                      }}
                      className="p-2 rounded-lg transition-all duration-200"
                      style={{
                        background: 'rgba(231, 76, 60, 0.2)',
                        border: '1px solid rgba(231, 76, 60, 0.4)',
                        color: '#E74C3C'
                      }}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </motion.button>
                  </div>
                </motion.div>
              );
            })
          )}
        </AnimatePresence>
      </div>

      {/* Edit Popper */}
      {editingId && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          ref={setPopperElement}
          style={{
            ...styles.popper,
            background: 'linear-gradient(135deg, #2C3E50, #34495E)',
            border: '2px solid #F1C40F',
            fontFamily: '"Share Tech Mono", monospace',
            boxShadow: '0 0 20px rgba(241, 196, 15, 0.4)'
          }}
          {...attributes.popper}
          className="rounded-lg shadow-xl p-4 z-50 w-72"
        >
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center gap-2 mb-3">
              <span>✏️</span>
              <h4 className="font-bold" style={{color: '#F1C40F'}}>EDIT PARTICIPANT</h4>
            </div>
            
            {/* Name Input */}
            <div>
              <label className="block text-sm font-bold mb-2" style={{color: '#ECF0F1'}}>
                PARTICIPANT NAME
              </label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg font-bold"
                style={{
                  background: 'rgba(52, 73, 94, 0.8)',
                  border: '2px solid rgba(241, 196, 15, 0.3)',
                  color: '#ECF0F1',
                  fontFamily: '"Share Tech Mono", monospace'
                }}
                autoFocus
              />
            </div>
            
            {/* Email Display */}
            <div>
              <label className="block text-sm font-bold mb-2" style={{color: '#ECF0F1'}}>
                EMAIL
              </label>
              <div className="px-3 py-2 rounded-lg text-sm" style={{
                background: 'rgba(52, 73, 94, 0.6)',
                border: '1px solid rgba(149, 165, 166, 0.3)',
                color: '#95A5A6'
              }}>
                {editEmail || 'No email provided'}
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setEditingId(null);
                  setEditName('');
                  setEditEmail('');
                }}
                className="px-4 py-2 rounded-lg font-bold text-sm"
                style={{
                  background: 'rgba(52, 73, 94, 0.8)',
                  border: '1px solid rgba(149, 165, 166, 0.3)',
                  color: '#95A5A6'
                }}
              >
                CANCEL
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSave}
                className="px-4 py-2 rounded-lg font-bold text-sm"
                style={{
                  background: 'linear-gradient(135deg, #27AE60, #2ECC71)',
                  border: '2px solid #F1C40F',
                  color: '#FFFFFF',
                  textShadow: '1px 1px 0px #1E8449'
                }}
              >
                ⚡ SAVE
              </motion.button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <ModalBasic
          id="delete-confirmation-modal"
          modalOpen={modalOpen}
          setModalOpen={handleCloseModal}
          title="⚔️ CONFIRM LEADERBOARD REMOVAL"
        >
          <div className="p-6" style={{
            background: 'linear-gradient(135deg, #2C3E50, #34495E)',
            fontFamily: '"Share Tech Mono", monospace'
          }}>
            <div className="mb-6 text-center">
              <div className="text-4xl mb-3">⚠️</div>
              <p className="font-bold" style={{color: '#ECF0F1'}}>
                Are you sure you want to remove this participant from the leaderboard?
              </p>
              <p className="text-sm mt-2" style={{color: '#95A5A6'}}>
                This action cannot be undone!
              </p>
            </div>

            <div className="flex justify-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="button"
                className="px-6 py-3 rounded-lg font-bold"
                style={{
                  background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                  border: '2px solid #F1C40F',
                  color: '#FFFFFF',
                  textShadow: '1px 1px 0px #A93226'
                }}
                onClick={handleDelete}
              >
                🗑️ DELETE PARTICIPANT
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="button"
                className="px-6 py-3 rounded-lg font-bold"
                style={{
                  background: 'rgba(52, 73, 94, 0.8)',
                  border: '1px solid rgba(149, 165, 166, 0.3)',
                  color: '#95A5A6'
                }}
                onClick={handleCloseModal}
              >
                CANCEL
              </motion.button>
            </div>
          </div>
        </ModalBasic>
      )}
    </motion.div>
  );
};

export default ManageLeaderboard; 